import React from 'react';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import { Card } from '../ui';
import { fmtUSDFull, fmtNum } from '../../data/districts';

// Sample attendance trend + revenue-by-category panel for a district demo.
const DistrictAnalytics = ({ district, stats }) => {
  const { primary, secondary, accent } = district.colors;
  const totalRev = stats.revenueByCategory.reduce((s, c) => s + c.value, 0);
  const catColors = [primary, secondary, accent];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Attendance trend */}
      <Card className="p-5 lg:col-span-2">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-bold font-display text-ink-900">Attendance trend</h3>
          <span className="text-xs text-ink-400">Fans per month · sample</span>
        </div>
        <p className="text-2xl font-bold font-display text-ink-900 mb-3">
          {fmtNum(stats.avgAttendance)}{' '}
          <span className="text-sm font-medium text-ink-400">avg / event</span>
        </p>
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={stats.attendanceTrend} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id={`grad-${district.slug}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={secondary} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={secondary} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false} width={40}
                tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip
                formatter={(v) => [`${fmtNum(v)} fans`, 'Attendance']}
                contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0', fontSize: 13 }}
              />
              <Area type="monotone" dataKey="fans" stroke={secondary} strokeWidth={2.5}
                fill={`url(#grad-${district.slug})`} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Revenue by category */}
      <Card className="p-5">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-bold font-display text-ink-900">Revenue mix</h3>
          <span className="text-xs text-ink-400">YTD · sample</span>
        </div>
        <p className="text-2xl font-bold font-display text-ink-900 mb-4">{fmtUSDFull(totalRev)}</p>
        <div className="space-y-4">
          {stats.revenueByCategory.map((c, i) => {
            const pct = Math.round((c.value / totalRev) * 100);
            return (
              <div key={c.name}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="flex items-center gap-2 text-ink-700 font-medium">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: catColors[i] }} />
                    {c.name}
                  </span>
                  <span className="text-ink-500">{fmtUSDFull(c.value)}</span>
                </div>
                <div className="h-2 rounded-full bg-ink-100 overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: catColors[i] }} />
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
};

export default DistrictAnalytics;
